// API client for backend communication

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

interface ApiOptions {
  method?: string
  body?: unknown
  headers?: Record<string, string>
}

class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown,
  ) {
    super(`${status} ${statusText}`)
    this.name = 'ApiError'
  }
}

async function apiRequest<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const token = localStorage.getItem('auth_token')

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: options.method || 'GET',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  })

  if (!response.ok) {
    const data = await response.json().catch(() => null)
    throw new ApiError(response.status, response.statusText, data)
  }

  // Handle 204 No Content responses
  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

// Auth API
export const authApi = {
  login: (oktaToken: string) =>
    apiRequest<{
      token: string
      expiresAt: string
      user: User
    }>('/api/auth/login', {
      method: 'POST',
      body: { oktaToken },
    }),

  me: () => apiRequest<{ user: User }>('/api/auth/me'),

  refresh: () =>
    apiRequest<{ token: string; expiresAt: string }>('/api/auth/refresh', {
      method: 'POST',
    }),

  logout: () =>
    apiRequest<{ message: string }>('/api/auth/logout', {
      method: 'POST',
    }),
}

// Users API
export const usersApi = {
  list: (page = 1, pageSize = 50) =>
    apiRequest<{
      users: User[]
      total: number
      page: number
      pageSize: number
    }>(`/api/users?page=${page}&pageSize=${pageSize}`),

  get: (id: string) => apiRequest<User>(`/api/users/${id}`),

  create: (data: { email: string; displayName?: string; role?: RoleName }) =>
    apiRequest<User>('/api/users', {
      method: 'POST',
      body: data,
    }),

  update: (
    id: string,
    data: { email?: string; displayName?: string; role?: RoleName; isActive?: boolean },
  ) =>
    apiRequest<User>(`/api/users/${id}`, {
      method: 'PUT',
      body: data,
    }),

  updateRole: (id: string, role: RoleName) =>
    apiRequest<User>(`/api/users/${id}/role`, {
      method: 'PUT',
      body: { role },
    }),

  updateStatus: (id: string, isActive: boolean) =>
    apiRequest<User>(`/api/users/${id}/status`, {
      method: 'PUT',
      body: { isActive },
    }),
}

// Settings API
export const settingsApi = {
  list: () => apiRequest<{ settings: Setting[] }>('/api/settings'),

  get: (key: string) => apiRequest<Setting>(`/api/settings/${key}`),

  update: (key: string, value: string, description?: string) =>
    apiRequest<Setting>(`/api/settings/${key}`, {
      method: 'PUT',
      body: { value, description },
    }),
}

// Kubernetes API
export const k8sApi = {
  namespaces: () => apiRequest<{ namespaces: string[] }>('/api/k8s/namespaces'),

  pods: (namespace?: string) =>
    apiRequest<{
      pods: Pod[]
      total: number
      namespace?: string
    }>(`/api/k8s/pods${namespace ? `?namespace=${namespace}` : ''}`),

  podLogs: (
    namespace: string,
    podName: string,
    options?: {
      container?: string
      tailLines?: number
      sinceSeconds?: number
      previous?: boolean
    },
  ) => {
    const params = new URLSearchParams()
    if (options?.container) params.set('container', options.container)
    if (options?.tailLines) params.set('tailLines', String(options.tailLines))
    if (options?.sinceSeconds) params.set('sinceSeconds', String(options.sinceSeconds))
    if (options?.previous) params.set('previous', 'true')
    const query = params.toString()
    return apiRequest<{
      namespace: string
      podName: string
      container?: string
      logs: string
      timestamp: string
    }>(`/api/k8s/pods/${namespace}/${podName}/logs${query ? `?${query}` : ''}`)
  },

  restartPod: (namespace: string, podName: string) =>
    apiRequest<{
      success: boolean
      message: string
      namespace: string
      podName: string
    }>(`/api/k8s/pods/${namespace}/${podName}/restart`, {
      method: 'POST',
    }),

  killPod: (namespace: string, podName: string) =>
    apiRequest<{
      success: boolean
      message: string
      namespace: string
      podName: string
    }>(`/api/k8s/pods/${namespace}/${podName}`, {
      method: 'DELETE',
    }),

  // ConfigMaps
  configMaps: (namespace?: string) =>
    apiRequest<{
      configMaps: ConfigMap[]
      total: number
      namespace?: string
    }>(`/api/k8s/configmaps${namespace ? `?namespace=${namespace}` : ''}`),

  configMap: (namespace: string, name: string) =>
    apiRequest<ConfigMap>(`/api/k8s/configmaps/${namespace}/${name}`),

  createConfigMap: (namespace: string, data: CreateConfigMapRequest) =>
    apiRequest<ConfigMap>(`/api/k8s/configmaps/${namespace}`, {
      method: 'POST',
      body: data,
    }),

  updateConfigMap: (namespace: string, name: string, data: UpdateConfigMapRequest) =>
    apiRequest<ConfigMap>(`/api/k8s/configmaps/${namespace}/${name}`, {
      method: 'PUT',
      body: data,
    }),

  deleteConfigMap: (namespace: string, name: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/k8s/configmaps/${namespace}/${name}`, {
      method: 'DELETE',
    }),

  // Secrets
  secrets: (namespace?: string) =>
    apiRequest<{
      secrets: Secret[]
      total: number
      namespace?: string
    }>(`/api/k8s/secrets${namespace ? `?namespace=${namespace}` : ''}`),

  secret: (namespace: string, name: string) =>
    apiRequest<Secret>(`/api/k8s/secrets/${namespace}/${name}`),

  revealSecret: (namespace: string, name: string) =>
    apiRequest<Secret>(`/api/k8s/secrets/${namespace}/${name}/reveal`),

  createSecret: (namespace: string, data: CreateSecretRequest) =>
    apiRequest<Secret>(`/api/k8s/secrets/${namespace}`, {
      method: 'POST',
      body: data,
    }),

  updateSecret: (namespace: string, name: string, data: UpdateSecretRequest) =>
    apiRequest<Secret>(`/api/k8s/secrets/${namespace}/${name}`, {
      method: 'PUT',
      body: data,
    }),

  deleteSecret: (namespace: string, name: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/k8s/secrets/${namespace}/${name}`, {
      method: 'DELETE',
    }),

  // Deployments
  deployments: (namespace?: string) =>
    apiRequest<{
      deployments: Deployment[]
      total: number
      namespace?: string
    }>(`/api/k8s/deployments${namespace ? `?namespace=${namespace}` : ''}`),

  deployment: (namespace: string, name: string) =>
    apiRequest<Deployment>(`/api/k8s/deployments/${namespace}/${name}`),

  deploymentManifest: (namespace: string, name: string) =>
    apiRequest<{ manifest: string }>(`/api/k8s/deployments/${namespace}/${name}/manifest`),

  createDeployment: (namespace: string, request: CreateDeploymentRequest) =>
    apiRequest<Deployment>(`/api/k8s/deployments/${namespace}`, {
      method: 'POST',
      body: request,
    }),

  scaleDeployment: (namespace: string, name: string, replicas: number) =>
    apiRequest<Deployment>(`/api/k8s/deployments/${namespace}/${name}/scale`, {
      method: 'POST',
      body: { replicas },
    }),

  restartDeployment: (namespace: string, name: string) =>
    apiRequest<{ success: boolean; message: string }>(
      `/api/k8s/deployments/${namespace}/${name}/restart`,
      {
        method: 'POST',
      },
    ),

  deleteDeployment: (namespace: string, name: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/k8s/deployments/${namespace}/${name}`, {
      method: 'DELETE',
    }),

  // Services
  services: (namespace?: string) =>
    apiRequest<{
      services: K8sService[]
      total: number
      namespace?: string
    }>(`/api/k8s/services${namespace ? `?namespace=${namespace}` : ''}`),

  service: (namespace: string, name: string) =>
    apiRequest<K8sService>(`/api/k8s/services/${namespace}/${name}`),

  deleteK8sService: (namespace: string, name: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/k8s/services/${namespace}/${name}`, {
      method: 'DELETE',
    }),

  // Helm releases
  helmReleases: (namespace?: string) =>
    apiRequest<{
      releases: HelmRelease[]
      total: number
      namespace?: string
    }>(`/api/k8s/helm/releases${namespace ? `?namespace=${namespace}` : ''}`),

  helmRelease: (namespace: string, name: string) =>
    apiRequest<HelmReleaseDetail>(`/api/k8s/helm/releases/${namespace}/${name}`),

  helmReleaseHistory: (namespace: string, name: string) =>
    apiRequest<{
      name: string
      namespace: string
      history: HelmReleaseHistory[]
    }>(`/api/k8s/helm/releases/${namespace}/${name}/history`),

  installHelmRelease: (namespace: string, request: InstallHelmReleaseRequest) =>
    apiRequest<HelmActionResponse>(`/api/k8s/helm/releases/${namespace}`, {
      method: 'POST',
      body: request,
    }),

  upgradeHelmRelease: (namespace: string, name: string, request: UpgradeHelmReleaseRequest) =>
    apiRequest<HelmActionResponse>(`/api/k8s/helm/releases/${namespace}/${name}`, {
      method: 'PUT',
      body: request,
    }),

  rollbackHelmRelease: (namespace: string, name: string, request: RollbackHelmReleaseRequest) =>
    apiRequest<HelmActionResponse>(`/api/k8s/helm/releases/${namespace}/${name}/rollback`, {
      method: 'POST',
      body: request,
    }),

  uninstallHelmRelease: (namespace: string, name: string) =>
    apiRequest<HelmActionResponse>(`/api/k8s/helm/releases/${namespace}/${name}`, {
      method: 'DELETE',
    }),

  // Helm repos
  helmRepos: () => apiRequest<{ repos: HelmRepo[] }>('/api/k8s/helm/repos'),

  addHelmRepo: (request: AddHelmRepoRequest) =>
    apiRequest<HelmActionResponse>('/api/k8s/helm/repos', {
      method: 'POST',
      body: request,
    }),

  removeHelmRepo: (name: string) =>
    apiRequest<HelmActionResponse>(`/api/k8s/helm/repos/${name}`, {
      method: 'DELETE',
    }),

  updateHelmRepos: () =>
    apiRequest<HelmActionResponse>('/api/k8s/helm/repos/update', {
      method: 'POST',
    }),

  // HiveMQ Log Files
  hivemqPods: () => apiRequest<{ pods: HiveMQPod[] }>('/api/k8s/hivemq/pods'),

  hivemqLogFiles: (podName: string) => apiRequest<LogFileTree>(`/api/k8s/hivemq/${podName}/files`),

  hivemqLogContent: (podName: string, filePath: string, tailLines = 1000) => {
    const params = new URLSearchParams()
    if (tailLines !== 1000) params.set('tailLines', String(tailLines))
    const query = params.toString()
    return apiRequest<LogFileContent>(
      `/api/k8s/hivemq/${podName}/files/${filePath}${query ? `?${query}` : ''}`,
    )
  },

  hivemqLogDownload: async (podName: string, filePath: string): Promise<Blob> => {
    const token = localStorage.getItem('auth_token')
    const headers: Record<string, string> = {}
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    const response = await fetch(`${API_BASE_URL}/api/k8s/hivemq/${podName}/download/${filePath}`, {
      headers,
    })

    if (!response.ok) {
      throw new ApiError(response.status, response.statusText)
    }

    return response.blob()
  },
}

// AI API (Google Gemini only)
export const aiApi = {
  gemini: (promptinit: string, query: string) =>
    apiRequest<{ response: string; provider: string }>('/api/ai/gemini', {
      method: 'POST',
      body: { promptinit, query },
    }),

  analyzeImage: (request: ImageAnalysisRequest) =>
    apiRequest<ImageAnalysisResponse>('/api/ai/analyze-image', {
      method: 'POST',
      body: request,
    }),

  playgroundChat: (request: PlaygroundChatRequest) =>
    apiRequest<PlaygroundChatResponse>('/api/ai/playground-chat', {
      method: 'POST',
      body: request,
    }),
}

// Playground Chat types
export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp?: number
}

export interface PlaygroundChatRequest {
  message: string
  playgroundState: { nodes: unknown[]; edges: unknown[] }
  conversationHistory: ChatMessage[]
  imageBase64?: string
  imageMimeType?: string
}

export type PlaygroundAction =
  | { type: 'add_node'; slug: string; name: string; position: { x: number; y: number } }
  | { type: 'remove_node'; nodeId: string }
  | { type: 'add_edge'; sourceId: string; targetId: string; label?: string }
  | { type: 'remove_edge'; edgeId: string }
  | { type: 'update_node'; nodeId: string; name?: string; position?: { x: number; y: number } }

export interface PlaygroundChatResponse {
  message: string
  actions: PlaygroundAction[]
  actionSummary?: string
  model: string
}

// Image Analysis types
export interface ImageAnalysisRequest {
  imageBase64: string
  mimeType: string
  availableNodeTypes?: NodeTypeInfoForAi[] // Now optional - backend fetches from database
  availableEdgeTypes?: string[]
}

export interface NodeTypeInfoForAi {
  slug: string
  name: string
  category?: string
  description?: string
}

export interface ExtractedNode {
  id: string
  type: string
  position: { x: number; y: number }
  data: {
    label: string
    name: string
    logoUrl?: string
    backgroundColor?: string
    externalLink?: string
  }
}

export interface ExtractedEdge {
  id: string
  source: string
  target: string
  type: string
  animated: boolean
  style?: { stroke: string }
  markerEnd?: { type: string; color: string }
  data?: { label?: string; color?: string; bold?: boolean }
}

export interface ImageAnalysisResponse {
  nodes: ExtractedNode[]
  edges: ExtractedEdge[]
  model: string
  provider: string
}

// Audit API
export const auditApi = {
  list: (params?: {
    userId?: string
    action?: string
    resourceType?: string
    startDate?: string
    endDate?: string
    page?: number
    pageSize?: number
  }) => {
    const searchParams = new URLSearchParams()
    if (params?.userId) searchParams.set('userId', params.userId)
    if (params?.action) searchParams.set('action', params.action)
    if (params?.resourceType) searchParams.set('resourceType', params.resourceType)
    if (params?.startDate) searchParams.set('startDate', params.startDate)
    if (params?.endDate) searchParams.set('endDate', params.endDate)
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize))
    const query = searchParams.toString()
    return apiRequest<{
      logs: AuditLog[]
      total: number
      page: number
      pageSize: number
    }>(`/api/audit${query ? `?${query}` : ''}`)
  },

  recent: (limit = 100) =>
    apiRequest<{
      logs: AuditLog[]
      total: number
      page: number
      pageSize: number
    }>(`/api/audit/recent?limit=${limit}`),
}

// User Preferences API
export const preferencesApi = {
  list: () => apiRequest<{ preferences: UserPreference[] }>('/api/preferences'),

  get: (key: string) => apiRequest<UserPreference>(`/api/preferences/${key}`),

  set: (key: string, value: string) =>
    apiRequest<UserPreference>(`/api/preferences/${key}`, {
      method: 'PUT',
      body: { value },
    }),

  delete: (key: string) =>
    apiRequest<{ message: string }>(`/api/preferences/${key}`, {
      method: 'DELETE',
    }),
}

// Types
export type RoleName = 'user' | 'operator' | 'admin'

export interface User {
  id: string
  oktaId: string
  email: string
  displayName?: string
  role: RoleName
  isActive: boolean
  createdAt: string
  updatedAt: string
  lastLoginAt?: string
}

export interface Setting {
  id: number
  key: string
  value: string
  description?: string
  createdAt: string
  updatedAt: string
}

export interface Container {
  name: string
  image: string
  ready: boolean
  restartCount: number
  state: string
}

export interface Pod {
  name: string
  namespace: string
  status: string
  ready: string
  restarts: number
  age: string
  nodeName?: string
  podIP?: string
  containers: Container[]
}

export interface ConfigMap {
  name: string
  namespace: string
  data: Record<string, string>
  labels?: Record<string, string>
  annotations?: Record<string, string>
  createdAt: string
  age: string
}

export interface CreateConfigMapRequest {
  name: string
  data: Record<string, string>
  labels?: Record<string, string>
  annotations?: Record<string, string>
}

export interface UpdateConfigMapRequest {
  data?: Record<string, string>
  labels?: Record<string, string>
  annotations?: Record<string, string>
}

export interface Secret {
  name: string
  namespace: string
  type: string
  data: Record<string, string>
  labels?: Record<string, string>
  annotations?: Record<string, string>
  createdAt: string
  age: string
}

export interface CreateSecretRequest {
  name: string
  type?: string
  data: Record<string, string>
  labels?: Record<string, string>
  annotations?: Record<string, string>
}

export interface UpdateSecretRequest {
  data?: Record<string, string>
  labels?: Record<string, string>
  annotations?: Record<string, string>
}

export interface Deployment {
  name: string
  namespace: string
  replicas: number
  readyReplicas: number
  availableReplicas: number
  updatedReplicas: number
  strategy: string
  labels?: Record<string, string>
  annotations?: Record<string, string>
  selector?: Record<string, string>
  createdAt: string
  age: string
}

export interface ContainerPort {
  name?: string
  containerPort: number
  protocol?: string
}

export interface ContainerResources {
  requests?: Record<string, string>
  limits?: Record<string, string>
}

export interface CreateContainerSpec {
  name: string
  image: string
  ports?: ContainerPort[]
  resources?: ContainerResources
}

export interface CreateDeploymentRequest {
  name: string
  replicas?: number
  selector: Record<string, string>
  containers: CreateContainerSpec[]
  labels?: Record<string, string>
}

export interface ServicePort {
  name?: string
  protocol: string
  port: number
  targetPort?: string
  nodePort?: number
}

export interface K8sService {
  name: string
  namespace: string
  type: string
  clusterIP?: string
  externalIP?: string
  ports: ServicePort[]
  selector?: Record<string, string>
  labels?: Record<string, string>
  annotations?: Record<string, string>
  createdAt: string
  age: string
}

// Helm types
export interface HelmRelease {
  name: string
  namespace: string
  revision: number
  updated: string
  status: string
  chart: string
  chartVersion: string
  appVersion?: string
  description?: string
}

export interface HelmReleaseHistory {
  revision: number
  updated: string
  status: string
  chart: string
  appVersion: string
  description: string
}

export interface HelmReleaseDetail {
  release: HelmRelease
  values?: Record<string, string>
  notes?: string
}

export interface HelmRepo {
  name: string
  url: string
}

export interface InstallHelmReleaseRequest {
  name: string
  chart: string
  repo?: string
  version?: string
  values?: Record<string, string>
  valuesYaml?: string
}

export interface UpgradeHelmReleaseRequest {
  chart?: string
  version?: string
  values?: Record<string, string>
  reuseValues?: boolean
}

export interface RollbackHelmReleaseRequest {
  revision: number
}

export interface AddHelmRepoRequest {
  name: string
  url: string
  username?: string
  password?: string
}

export interface HelmActionResponse {
  success: boolean
  message: string
  namespace?: string
  releaseName?: string
}

// HiveMQ Log File types
export interface HiveMQPod {
  name: string
  namespace: string
  status: string
  ready: boolean
}

export interface LogFileEntry {
  name: string
  path: string
  isDirectory: boolean
  size: number | null
  modifiedAt: string | null
  children?: LogFileEntry[]
}

export interface LogFileTree {
  podName: string
  basePath: string
  entries: LogFileEntry[]
}

export interface LogFileContent {
  podName: string
  filePath: string
  content: string
  lineCount: number
  truncated: boolean
  fileSize: number
  timestamp: string
}

export interface AuditLog {
  id: number
  userId?: string
  userEmail?: string
  action: string
  resourceType?: string
  resourceId?: string
  details?: Record<string, unknown>
  ipAddress?: string
  createdAt: string
}

export interface UserPreference {
  id: number
  userId: string
  preferenceKey: string
  preferenceValue: string
  createdAt: string
  updatedAt: string
}

// Activity API
export const activityApi = {
  // Log a page visit
  log: (pagePath: string, pageTitle?: string, sessionId?: string) =>
    apiRequest<ActivityLog>('/api/activity', {
      method: 'POST',
      body: { pagePath, pageTitle, sessionId, activityType: 'page_view' },
    }),

  // Log an external link click
  logExternalLink: (params: {
    pagePath: string
    pageTitle?: string
    sessionId?: string
    externalUrl: string
    linkName: string
  }) =>
    apiRequest<ActivityLog>('/api/activity', {
      method: 'POST',
      body: {
        pagePath: params.pagePath,
        pageTitle: params.pageTitle,
        sessionId: params.sessionId,
        activityType: 'external_link',
        externalUrl: params.externalUrl,
        linkName: params.linkName,
      },
    }),

  // Get activity logs (admin only)
  list: (params?: {
    page?: number
    pageSize?: number
    userEmail?: string
    pagePath?: string
    startDate?: string
    endDate?: string
  }) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize))
    if (params?.userEmail) searchParams.set('userEmail', params.userEmail)
    if (params?.pagePath) searchParams.set('pagePath', params.pagePath)
    if (params?.startDate) searchParams.set('startDate', params.startDate)
    if (params?.endDate) searchParams.set('endDate', params.endDate)
    const query = searchParams.toString()
    return apiRequest<ActivityLogListResponse>(`/api/activity${query ? `?${query}` : ''}`)
  },

  // Get activity statistics (admin only)
  stats: (days = 7) => apiRequest<ActivityStats>(`/api/activity/stats?days=${days}`),

  // Get all users by page views (admin only)
  users: (params?: { days?: number; page?: number; pageSize?: number }) => {
    const searchParams = new URLSearchParams()
    if (params?.days) searchParams.set('days', String(params.days))
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize))
    const query = searchParams.toString()
    return apiRequest<UserActivityListResponse>(`/api/activity/users${query ? `?${query}` : ''}`)
  },

  // Get all pages by views (admin only)
  pages: (params?: { days?: number; page?: number; pageSize?: number }) => {
    const searchParams = new URLSearchParams()
    if (params?.days) searchParams.set('days', String(params.days))
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize))
    const query = searchParams.toString()
    return apiRequest<PageActivityListResponse>(`/api/activity/pages${query ? `?${query}` : ''}`)
  },

  // Get all external links by clicks (admin only)
  externalLinks: (params?: { days?: number; page?: number; pageSize?: number }) => {
    const searchParams = new URLSearchParams()
    if (params?.days) searchParams.set('days', String(params.days))
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize))
    const query = searchParams.toString()
    return apiRequest<ExternalLinkActivityListResponse>(
      `/api/activity/external-links${query ? `?${query}` : ''}`,
    )
  },
}

// Activity types
export interface ActivityLog {
  id: number
  userId?: string
  userEmail?: string
  activityType: string
  pagePath: string
  pageTitle?: string
  externalUrl?: string
  linkName?: string
  userAgent?: string
  ipAddress?: string
  sessionId?: string
  createdAt: string
}

export interface ActivityLogListResponse {
  logs: ActivityLog[]
  total: number
  page: number
  pageSize: number
}

export interface ActivityStats {
  totalPageViews: number
  totalExternalLinkClicks: number
  uniqueUsers: number
  topUsers: UserActivityCount[]
  topPages: PageActivityCount[]
  topExternalLinks: ExternalLinkActivityCount[]
  recentActivity: ActivityLog[]
}

export interface UserActivityCount {
  userEmail: string
  pageViews: number
}

export interface PageActivityCount {
  pagePath: string
  pageTitle?: string
  pageViews: number
}

export interface UserActivityListResponse {
  users: UserActivityCount[]
  total: number
  page: number
  pageSize: number
}

export interface PageActivityListResponse {
  pages: PageActivityCount[]
  total: number
  page: number
  pageSize: number
}

export interface ExternalLinkActivityCount {
  linkName: string
  externalUrl: string
  clicks: number
}

export interface ExternalLinkActivityListResponse {
  links: ExternalLinkActivityCount[]
  total: number
  page: number
  pageSize: number
}

// Customer types
export interface CustomerSummary {
  id: string
  slug: string
  name: string
  industry: string
  description?: string
  latitude?: number
  longitude?: number
  color: string
  isActive: boolean
  displayOnHome: boolean
  displayOnMap: boolean
  hasLogo: boolean
  hasCover: boolean
}

export interface CustomerDetail {
  id: string
  slug: string
  name: string
  industry: string
  description?: string
  challenge?: string
  solution?: string
  results?: string[]
  latitude?: number
  longitude?: number
  color: string
  isActive: boolean
  displayOnHome: boolean
  displayOnMap: boolean
  logoUrl?: string
  coverUrl?: string
  createdAt: string
  updatedAt: string
}

export interface CustomerMapData {
  id: string
  slug: string
  name: string
  industry: string
  description?: string
  latitude: number
  longitude: number
  color: string
  logoUrl?: string
}

export interface CustomerHomeData {
  id: string
  slug: string
  name: string
  industry: string
  description?: string
  logoUrl?: string
}

export interface CustomerListResponse {
  customers: CustomerSummary[]
  total: number
  page: number
  pageSize: number
}

export interface CreateCustomerRequest {
  slug: string
  name: string
  industry: string
  description?: string
  challenge?: string
  solution?: string
  results?: string[]
  latitude?: number
  longitude?: number
  color?: string
  displayOnHome?: boolean
  displayOnMap?: boolean
}

export interface UpdateCustomerRequest {
  slug?: string
  name?: string
  industry?: string
  description?: string
  challenge?: string
  solution?: string
  results?: string[]
  latitude?: number
  longitude?: number
  color?: string
  isActive?: boolean
  displayOnHome?: boolean
  displayOnMap?: boolean
}

// Customers API
export const customersApi = {
  // List all customers
  list: (params?: {
    page?: number
    pageSize?: number
    activeOnly?: boolean
    industry?: string
    search?: string
  }) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize))
    if (params?.activeOnly !== undefined) searchParams.set('activeOnly', String(params.activeOnly))
    if (params?.industry) searchParams.set('industry', params.industry)
    if (params?.search) searchParams.set('search', params.search)
    const query = searchParams.toString()
    return apiRequest<CustomerListResponse>(`/api/customers${query ? `?${query}` : ''}`)
  },

  // Get customer by ID
  get: (id: string) => apiRequest<CustomerDetail>(`/api/customers/${id}`),

  // Get customer by slug
  getBySlug: (slug: string) => apiRequest<CustomerDetail>(`/api/customers/slug/${slug}`),

  // Get customers for home page
  getForHome: () => apiRequest<CustomerHomeData[]>('/api/customers/home'),

  // Get customers for map
  getForMap: () => apiRequest<CustomerMapData[]>('/api/customers/map'),

  // Create customer (admin only)
  create: (data: CreateCustomerRequest) =>
    apiRequest<CustomerDetail>('/api/customers', {
      method: 'POST',
      body: data,
    }),

  // Update customer (admin only)
  update: (id: string, data: UpdateCustomerRequest) =>
    apiRequest<CustomerDetail>(`/api/customers/${id}`, {
      method: 'PUT',
      body: data,
    }),

  // Delete customer (admin only)
  delete: (id: string) =>
    apiRequest<{ message: string }>(`/api/customers/${id}`, {
      method: 'DELETE',
    }),

  // Upload logo (admin only)
  uploadLogo: async (id: string, file: File) => {
    const token = localStorage.getItem('auth_token')
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${API_BASE_URL}/api/customers/${id}/logo`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    })

    if (!response.ok) {
      const data = await response.json().catch(() => null)
      throw new ApiError(response.status, response.statusText, data)
    }

    return response.json() as Promise<{ message: string }>
  },

  // Upload cover (admin only)
  uploadCover: async (id: string, file: File) => {
    const token = localStorage.getItem('auth_token')
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${API_BASE_URL}/api/customers/${id}/cover`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    })

    if (!response.ok) {
      const data = await response.json().catch(() => null)
      throw new ApiError(response.status, response.statusText, data)
    }

    return response.json() as Promise<{ message: string }>
  },

  // Delete logo (admin only)
  deleteLogo: (id: string) =>
    apiRequest<{ message: string }>(`/api/customers/${id}/logo`, {
      method: 'DELETE',
    }),

  // Delete cover (admin only)
  deleteCover: (id: string) =>
    apiRequest<{ message: string }>(`/api/customers/${id}/cover`, {
      method: 'DELETE',
    }),

  // Helper to get image URLs
  getLogoUrl: (id: string) => `${API_BASE_URL}/api/customers/${id}/logo`,
  getCoverUrl: (id: string) => `${API_BASE_URL}/api/customers/${id}/cover`,
}

// Playground Node types
export interface PlaygroundNodeSummary {
  id: string
  name: string
  slug: string
  category?: string
  description?: string
  backgroundColor: string
  externalLink?: string
  displayOrder: number
  isActive: boolean
  hasLogo: boolean
}

export interface PlaygroundNodeDetail {
  id: string
  name: string
  slug: string
  category?: string
  description?: string
  backgroundColor: string
  externalLink?: string
  displayOrder: number
  isActive: boolean
  logoUrl?: string
  createdAt: string
  updatedAt: string
}

export interface PlaygroundNodeMenuItem {
  id: string
  name: string
  slug: string
  category?: string
  backgroundColor: string
  externalLink?: string
  logoUrl?: string
}

export interface NodeCategory {
  name: string
  nodes: PlaygroundNodeMenuItem[]
}

export interface PlaygroundNodesMenuResponse {
  categories: NodeCategory[]
  uncategorized: PlaygroundNodeMenuItem[]
}

export interface PlaygroundNodeListResponse {
  nodes: PlaygroundNodeSummary[]
  total: number
  page: number
  pageSize: number
}

export interface CreatePlaygroundNodeRequest {
  name: string
  slug: string
  category?: string
  description?: string
  backgroundColor?: string
  externalLink?: string
  displayOrder?: number
}

export interface UpdatePlaygroundNodeRequest {
  name?: string
  slug?: string
  category?: string
  description?: string
  backgroundColor?: string
  externalLink?: string
  displayOrder?: number
  isActive?: boolean
}

// Playground Nodes API
export const playgroundNodesApi = {
  // List all nodes
  list: (params?: {
    page?: number
    pageSize?: number
    activeOnly?: boolean
    category?: string
    search?: string
  }) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize))
    if (params?.activeOnly !== undefined) searchParams.set('activeOnly', String(params.activeOnly))
    if (params?.category) searchParams.set('category', params.category)
    if (params?.search) searchParams.set('search', params.search)
    const query = searchParams.toString()
    return apiRequest<PlaygroundNodeListResponse>(
      `/api/playground-nodes${query ? `?${query}` : ''}`,
    )
  },

  // Get nodes grouped for menu
  getForMenu: () => apiRequest<PlaygroundNodesMenuResponse>('/api/playground-nodes/menu'),

  // Get all categories
  getCategories: () => apiRequest<{ categories: string[] }>('/api/playground-nodes/categories'),

  // Get node by ID
  get: (id: string) => apiRequest<PlaygroundNodeDetail>(`/api/playground-nodes/${id}`),

  // Get node by slug
  getBySlug: (slug: string) =>
    apiRequest<PlaygroundNodeDetail>(`/api/playground-nodes/slug/${slug}`),

  // Create node (admin only)
  create: (data: CreatePlaygroundNodeRequest) =>
    apiRequest<PlaygroundNodeDetail>('/api/playground-nodes', {
      method: 'POST',
      body: data,
    }),

  // Update node (admin only)
  update: (id: string, data: UpdatePlaygroundNodeRequest) =>
    apiRequest<PlaygroundNodeDetail>(`/api/playground-nodes/${id}`, {
      method: 'PUT',
      body: data,
    }),

  // Delete node (admin only)
  delete: (id: string) =>
    apiRequest<{ message: string }>(`/api/playground-nodes/${id}`, {
      method: 'DELETE',
    }),

  // Upload logo (admin only)
  uploadLogo: async (id: string, file: File) => {
    const token = localStorage.getItem('auth_token')
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${API_BASE_URL}/api/playground-nodes/${id}/logo`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    })

    if (!response.ok) {
      const data = await response.json().catch(() => null)
      throw new ApiError(response.status, response.statusText, data)
    }

    return response.json() as Promise<{ message: string }>
  },

  // Delete logo (admin only)
  deleteLogo: (id: string) =>
    apiRequest<{ message: string }>(`/api/playground-nodes/${id}/logo`, {
      method: 'DELETE',
    }),

  // Helper to get logo URL
  getLogoUrl: (id: string) => `${API_BASE_URL}/api/playground-nodes/${id}/logo`,
}

// Saved Playground types
export interface SavedPlaygroundSummary {
  id: string
  name: string
  version: number
  customerName?: string
  isPublic: boolean
  hasCustomerLogo: boolean
  createdByEmail?: string
  createdById?: string
  isOwner: boolean
  createdAt: string
  updatedAt: string
}

export interface SavedPlaygroundDetail {
  id: string
  name: string
  version: number
  nodes: unknown[]
  edges: unknown[]
  customerName?: string
  customerLogoUrl?: string
  isPublic: boolean
  createdByEmail?: string
  createdById?: string
  isOwner: boolean
  createdAt: string
  updatedAt: string
}

export interface SavedPlaygroundListResponse {
  playgrounds: SavedPlaygroundSummary[]
  total: number
  page: number
  pageSize: number
}

export interface CreateSavedPlaygroundRequest {
  name: string
  nodes: unknown[]
  edges: unknown[]
  customerName?: string
  isPublic?: boolean
}

export interface UpdateSavedPlaygroundRequest {
  name?: string
  nodes?: unknown[]
  edges?: unknown[]
  customerName?: string
  isPublic?: boolean
}

// Saved Playgrounds API
export const savedPlaygroundsApi = {
  // List playgrounds (own + public)
  list: (params?: { page?: number; pageSize?: number; search?: string; publicOnly?: boolean }) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize))
    if (params?.search) searchParams.set('search', params.search)
    if (params?.publicOnly !== undefined) searchParams.set('publicOnly', String(params.publicOnly))
    const query = searchParams.toString()
    return apiRequest<SavedPlaygroundListResponse>(`/api/playgrounds${query ? `?${query}` : ''}`)
  },

  // Get playground by ID
  get: (id: string) => apiRequest<SavedPlaygroundDetail>(`/api/playgrounds/${id}`),

  // Create playground
  create: (data: CreateSavedPlaygroundRequest) =>
    apiRequest<SavedPlaygroundDetail>('/api/playgrounds', {
      method: 'POST',
      body: data,
    }),

  // Update playground (owner only)
  update: (id: string, data: UpdateSavedPlaygroundRequest) =>
    apiRequest<SavedPlaygroundDetail>(`/api/playgrounds/${id}`, {
      method: 'PUT',
      body: data,
    }),

  // Delete playground (owner only)
  delete: (id: string) =>
    apiRequest<{ message: string }>(`/api/playgrounds/${id}`, {
      method: 'DELETE',
    }),

  // Upload customer logo (owner only)
  uploadLogo: async (id: string, file: File) => {
    const token = localStorage.getItem('auth_token')
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${API_BASE_URL}/api/playgrounds/${id}/logo`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    })

    if (!response.ok) {
      const data = await response.json().catch(() => null)
      throw new ApiError(response.status, response.statusText, data)
    }

    return response.json() as Promise<{ message: string }>
  },

  // Delete customer logo (owner only)
  deleteLogo: (id: string) =>
    apiRequest<{ message: string }>(`/api/playgrounds/${id}/logo`, {
      method: 'DELETE',
    }),

  // Helper to get logo URL
  getLogoUrl: (id: string) => `${API_BASE_URL}/api/playgrounds/${id}/logo`,

  // Create share link (owner only)
  createShareLink: (id: string) =>
    apiRequest<ShareLinkResponse>(`/api/playgrounds/${id}/share`, {
      method: 'POST',
    }),

  // Get share link info (owner only)
  getShareLink: (id: string) => apiRequest<ShareLinkResponse>(`/api/playgrounds/${id}/share`),

  // Revoke share link (owner only)
  revokeShareLink: (id: string) =>
    apiRequest<void>(`/api/playgrounds/${id}/share`, {
      method: 'DELETE',
    }),
}

// Share Link types
export interface ShareLinkResponse {
  shortCode: string
  shareUrl: string
  viewCount: number
  isActive: boolean
  expiresAt?: string
  createdAt: string
}

// Shared Playground types (for anonymous viewing)
export interface SharedPlaygroundResponse {
  name: string
  nodes: unknown[]
  edges: unknown[]
  customerName?: string
  customerLogoUrl?: string
  ownerName?: string
}

// Shared Playground API (no auth required)
export const sharedPlaygroundApi = {
  // Get shared playground by short code (anonymous)
  get: (shortCode: string) =>
    apiRequestNoAuth<SharedPlaygroundResponse>(`/api/shared/${shortCode}`),

  // Helper to get logo URL for shared playground
  getLogoUrl: (shortCode: string) => `${API_BASE_URL}/api/shared/${shortCode}/logo`,
}

// Helper for anonymous API requests
async function apiRequestNoAuth<T>(endpoint: string): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    const data = await response.json().catch(() => null)
    throw new ApiError(response.status, response.statusText, data)
  }

  return response.json() as Promise<T>
}

// Flow Template types
export interface FlowTemplate {
  id: string
  slug: string
  name: string
  description?: string
  industry?: string
  nodes: unknown[]
  edges: unknown[]
  thumbnailUrl?: string
  isActive: boolean
  displayOrder: number
  createdAt: string
  updatedAt: string
}

export interface FlowTemplateSummary {
  id: string
  slug: string
  name: string
  description?: string
  industry?: string
  thumbnailUrl?: string
  displayOrder: number
}

export interface CreateFlowTemplateRequest {
  slug: string
  name: string
  description?: string
  industry?: string
  nodes: unknown[]
  edges: unknown[]
  thumbnailUrl?: string
  isActive?: boolean
  displayOrder?: number
}

export interface UpdateFlowTemplateRequest {
  slug?: string
  name?: string
  description?: string
  industry?: string
  nodes?: unknown[]
  edges?: unknown[]
  thumbnailUrl?: string
  isActive?: boolean
  displayOrder?: number
}

// Flow Templates API
export const flowTemplatesApi = {
  // List all active templates (public)
  list: () => apiRequest<FlowTemplateSummary[]>('/api/templates'),

  // List all templates including inactive (admin only)
  listAll: () => apiRequest<FlowTemplate[]>('/api/templates/all'),

  // Get template by slug (public)
  getBySlug: (slug: string) => apiRequest<FlowTemplate>(`/api/templates/${slug}`),

  // Create template (admin only)
  create: (data: CreateFlowTemplateRequest) =>
    apiRequest<FlowTemplate>('/api/templates', {
      method: 'POST',
      body: data,
    }),

  // Update template (admin only)
  update: (id: string, data: UpdateFlowTemplateRequest) =>
    apiRequest<FlowTemplate>(`/api/templates/${id}`, {
      method: 'PUT',
      body: data,
    }),

  // Delete template (admin only)
  delete: (id: string) =>
    apiRequest<{ message: string }>(`/api/templates/${id}`, {
      method: 'DELETE',
    }),
}

// MQTT Connection Profile types (re-exported from mqtt-types for convenience)
export interface MqttConnectionProfile {
  id: string
  name: string
  protocol: 'mqtt' | 'mqtts' | 'ws' | 'wss'
  host: string
  port: number
  path?: string
  username?: string
  clientIdPrefix?: string
  tlsInsecure: boolean
  isPublic: boolean
  mqttVersion: 3 | 5
  cleanSession: boolean
  keepAlive: number
  maxMessages: number
  lwTopic?: string
  lwMessage?: string
  lwQos: 0 | 1 | 2
  lwRetain: boolean
  createdAt: string
  updatedAt: string
}

export interface MqttConnectionProfileListResponse {
  profiles: MqttConnectionProfile[]
  total: number
}

export interface CreateMqttProfileRequest {
  name: string
  protocol?: 'mqtt' | 'mqtts' | 'ws' | 'wss'
  host: string
  port?: number
  path?: string
  username?: string
  password?: string
  clientIdPrefix?: string
  tlsInsecure?: boolean
  mqttVersion?: 3 | 5
  cleanSession?: boolean
  keepAlive?: number
  maxMessages?: number
  lwTopic?: string
  lwMessage?: string
  lwQos?: 0 | 1 | 2
  lwRetain?: boolean
}

export interface UpdateMqttProfileRequest {
  name?: string
  protocol?: 'mqtt' | 'mqtts' | 'ws' | 'wss'
  host?: string
  port?: number
  path?: string
  username?: string
  password?: string
  clientIdPrefix?: string
  tlsInsecure?: boolean
  mqttVersion?: 3 | 5
  cleanSession?: boolean
  keepAlive?: number
  maxMessages?: number
  lwTopic?: string
  lwMessage?: string
  lwQos?: 0 | 1 | 2
  lwRetain?: boolean
}

export interface TestMqttConnectionRequest {
  protocol: 'mqtt' | 'mqtts' | 'ws' | 'wss'
  host: string
  port: number
  path?: string
  username?: string
  password?: string
  tlsInsecure?: boolean
  mqttVersion?: 3 | 5
  cleanSession?: boolean
  keepAlive?: number
}

export interface TestMqttConnectionResponse {
  success: boolean
  message: string
  latencyMs?: number
}

// MQTT Profiles API
export const mqttProfilesApi = {
  // List all profiles for current user (includes user's private + all public)
  list: () => apiRequest<MqttConnectionProfileListResponse>('/api/mqtt/profiles'),

  // Get profile by ID
  get: (id: string) => apiRequest<MqttConnectionProfile>(`/api/mqtt/profiles/${id}`),

  // Get password for a profile (used when connecting with a saved profile)
  getPassword: (id: string) =>
    apiRequest<{ password: string | null }>(`/api/mqtt/profiles/${id}/password`),

  // Create profile (private by default)
  create: (data: CreateMqttProfileRequest) =>
    apiRequest<MqttConnectionProfile>('/api/mqtt/profiles', {
      method: 'POST',
      body: data,
    }),

  // Update profile (only own private profiles)
  update: (id: string, data: UpdateMqttProfileRequest) =>
    apiRequest<MqttConnectionProfile>(`/api/mqtt/profiles/${id}`, {
      method: 'PUT',
      body: data,
    }),

  // Delete profile (only own private profiles)
  delete: (id: string) =>
    apiRequest<{ message: string }>(`/api/mqtt/profiles/${id}`, {
      method: 'DELETE',
    }),

  // Test connection (without saving)
  testConnection: (data: TestMqttConnectionRequest) =>
    apiRequest<TestMqttConnectionResponse>('/api/mqtt/test', {
      method: 'POST',
      body: data,
    }),

  // ==================== Admin-only Public Profile Endpoints ====================

  // List all public profiles (admin only)
  listPublic: () => apiRequest<MqttConnectionProfileListResponse>('/api/mqtt/profiles/public'),

  // Create public profile (admin only)
  createPublic: (data: CreateMqttProfileRequest) =>
    apiRequest<MqttConnectionProfile>('/api/mqtt/profiles/public', {
      method: 'POST',
      body: data,
    }),

  // Update public profile (admin only)
  updatePublic: (id: string, data: UpdateMqttProfileRequest) =>
    apiRequest<MqttConnectionProfile>(`/api/mqtt/profiles/public/${id}`, {
      method: 'PUT',
      body: data,
    }),

  // Delete public profile (admin only)
  deletePublic: (id: string) =>
    apiRequest<{ message: string }>(`/api/mqtt/profiles/public/${id}`, {
      method: 'DELETE',
    }),
}

// ==================== Packaging demo scenario ====================

export interface DeviationStatus {
  line: string
  machine: string
  degradedRate: number
  startedAt: string
  expiresAt: string
  minutesRemaining: number
  /** Onset ramp, so the UI can distinguish "ramping" from "sustained". */
  progressPct: number
  /**
   * How settled the plateau is. Only advances once the onset ramp completes, and separates
   * the collapsed transient from the throttled plateau - two phases that look nothing
   * alike on screen.
   */
  settlePct: number
  clearing: boolean
}

/**
 * Where the scenario is in its cycle, from the backend's `ScenarioPhase`.
 *
 * Shown on the presenter banner because two presenters demoing at once is the failure
 * mode that would actually embarrass someone (PRD v0.3 §11.3), and the first defence is
 * that anyone opening the page can see the environment is already mid-story.
 */
export type ScenarioPhase =
  | 'baseline'
  | 'onset'
  | 'stalling'
  | 'sustained'
  | 'recovering'
  | 'cooldown'

export interface ScenarioStatus {
  running: boolean
  /** `synthetic` (self-generated baseline) or `raw` (consuming the Edge branch). */
  sourceMode: string
  publishPrefix: string
  deviation?: DeviationStatus | null
  failure?: FailureStatus | null
  phase: ScenarioPhase
  cooldownRemainingSeconds: number
  linePerformance: Record<string, number>
}

export interface ArmDeviationRequest {
  line?: string
  machine?: string
  degradedRate?: number
  durationMinutes?: number
}

/**
 * An armed machine failure - the Availability half of the demo.
 *
 * Carries no rate: a failed machine is Held and produces nothing, so there is nothing
 * to degrade to. That is the whole point of keeping it apart from the deviation, which
 * is the Performance half.
 */
export interface FailureStatus {
  line: string
  machine: string
  startedAt: string
  expiresAt: string
  minutesRemaining: number
}

export interface ArmFailureRequest {
  line?: string
  /** Restricted server-side to the machines this publisher owns the state of. */
  machine?: string
  durationMinutes?: number
}

/**
 * Presenter controls for the Lisbon packaging demo.
 *
 * The deviation is armed server-side on purpose: it has to outlive the presenter's
 * browser tab, so the frontend never publishes to MQTT itself.
 */
export const scenarioApi = {
  // Publisher state, active deviation, cooldown and live line performance.
  getStatus: () => apiRequest<ScenarioStatus>('/api/scenario/status'),

  // Arm a deviation, or extend one already running (operator+).
  armDeviation: (data: ArmDeviationRequest = {}) =>
    apiRequest<DeviationStatus>('/api/scenario/deviation', {
      method: 'POST',
      body: data,
    }),

  // Clear the active deviation; the line ramps back to baseline (operator+).
  clearDeviation: () =>
    apiRequest<ScenarioStatus>('/api/scenario/deviation', {
      method: 'DELETE',
    }),

  // Arm a machine failure: the machine goes Held immediately (operator+).
  armFailure: (data: ArmFailureRequest = {}) =>
    apiRequest<FailureStatus>('/api/scenario/failure', {
      method: 'POST',
      body: data,
    }),

  // Clear an armed failure; the machine returns to its baseline state (operator+).
  clearFailure: () =>
    apiRequest<ScenarioStatus>('/api/scenario/failure', {
      method: 'DELETE',
    }),
}

// MQTT WebSocket proxy URL helper
export const getMqttProxyWsUrl = () => {
  const token = localStorage.getItem('auth_token')
  const baseUrl = API_BASE_URL || window.location.origin
  const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws'
  const wsBaseUrl = baseUrl.replace(/^https?/, wsProtocol)
  return `${wsBaseUrl}/api/mqtt/proxy${token ? `?token=${token}` : ''}`
}

export { ApiError }
